import SidebarTools from '@/components/shared/Sidebar/SidebarTools';
import SidebarViews, { HiddenVisibleLinksObj } from '@/components/shared/Sidebar/SidebarViews';
import { basicLinkStyles } from '@/components/shared/Sidebar/styles';
import { ApiProvider } from '@/provider/ApiProvider';
import {
  TaskAlt as ActivationIcon,
  Bookmark as ActivityIcon,
  BalanceOutlined as BalanceIcon,
  LocalOffer as BrandIcon,
  Compare as CompareIcon,
  Note as ContractIcon,
  Store as PropertyIcon,
  SpeedOutlined as SpeedIcon,
} from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import Box from '@mui/system/Box';
import React, { useEffect, useRef, useState } from 'react';

import SidebarLink, { SidebarLinkProps } from '@/components/shared/Sidebar/SidebarLink';

import { SidebarProps } from '@/components/shared/Sidebar/Sidebar';

const DynamicContentSections: React.FC<SidebarProps> = (props) => {
  const toolsLinks: SidebarLinkProps[] = [
    ...(Boolean(ApiProvider.permission('tools_compare'))
      ? [
          {
            to: '/comparison',
            icon: CompareIcon,
            text: 'Compare',
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
          },
        ]
      : []),
    ...(Boolean(ApiProvider.permission('tools_contract_approval'))
      ? [
          {
            to: 'https://app.fynk.com/documents#create',
            icon: BalanceIcon,
            text: 'Approval & Contract',
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
            isExternal: true,
            target: '_blank',
          },
        ]
      : []),
    ...(Boolean(ApiProvider.permission('tools_external_dashboard'))
      ? [
          {
            to: 'https://app.powerbi.com/home?ctid=2ddfb918-f0bd-4c31-93e8-c4e49aaa36b4',
            text: 'External Dashboard',
            icon: SpeedIcon,
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
            isExternal: true,
            target: '_blank',
          },
        ]
      : []),
  ];

  const viewsLinks: SidebarLinkProps[] = [
    ...(Boolean(ApiProvider.permission('contract_list_view'))
      ? [
          {
            to: '/listview/contract',
            text: 'Contract',
            icon: ContractIcon,
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
          },
        ]
      : []),
    ...(Boolean(ApiProvider.permission('campaign_list_view'))
      ? [
          {
            to: '/listview/activation',
            icon: ActivationIcon,
            text: 'Activation',
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
          },
        ]
      : []),
    ...(Boolean(ApiProvider.permission('activation_list_view'))
      ? [
          {
            to: '/listview/activities',
            icon: ActivityIcon,
            text: 'Activities',
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
          },
        ]
      : []),
    ...(Boolean(ApiProvider.permission('brand_list_view'))
      ? [
          {
            to: '/listview/brand',
            icon: BrandIcon,
            text: 'Brand',
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
          },
        ]
      : []),
    ...(Boolean(ApiProvider.permission('property_list_view'))
      ? [
          {
            to: '/listview/property',
            icon: PropertyIcon,
            text: 'Right Holder/Partner',
            boxContainerStyles: { ...basicLinkStyles },
            isVisible: false,
          },
        ]
      : []),
  ];

  const [tools, setTools] = useState<HiddenVisibleLinksObj>({ visible: [], hidden: toolsLinks });
  const [views, setViews] = useState<HiddenVisibleLinksObj>({ visible: [], hidden: viewsLinks });

  const containerRef = useRef(null);
  const toolsWrapperRef = useRef(null);
  const viewsWrapperRef = useRef(null);
  const stubToolsRef = useRef<Array<HTMLDivElement | null>>([]);
  const stubViewsRef = useRef<Array<HTMLDivElement | null>>([]);

  // Find the items that fit the container.
  useEffect(() => {
    call();

    // Bind the window resize event listener.
    window.addEventListener('resize', call);
    return () => window.removeEventListener('resize', call);
  }, [containerRef.current, toolsWrapperRef.current, props.compactSidebarMenus]);

  const call = () => {
    if (props.compactSidebarMenus) {
      setTools({ visible: [], hidden: toolsLinks });
      setViews({ visible: [], hidden: viewsLinks });
      return;
    }

    setTools({ visible: [], hidden: toolsLinks });
    setViews({ visible: [], hidden: viewsLinks });
    // setViews({visible: [], hidden: viewsLinks});

    setTimeout(() => {
      if (containerRef.current && viewsWrapperRef.current && toolsWrapperRef.current) {
        handleVisibleLinks(
          toolsLinks,
          setTools,
          stubToolsRef,
          // @ts-expect-error: We have a condition to prevent Null error here.
          containerRef.current.clientHeight,
          // @ts-expect-error: We have a condition to prevent Null error here.
          viewsWrapperRef.current.clientHeight + toolsWrapperRef.current.clientHeight,
        );

        setTimeout(() => {
          if (!containerRef.current || !viewsWrapperRef.current || !toolsWrapperRef.current) return;
          handleVisibleLinks(
            viewsLinks,
            setViews,
            stubViewsRef,
            // @ts-expect-error: We have a condition to prevent Null error here.
            containerRef.current.clientHeight,
            // @ts-expect-error: We have a condition to prevent Null error here.
            viewsWrapperRef.current.clientHeight + toolsWrapperRef.current.clientHeight,
          );
        }, 100);
      }
    }, 0);
  };

  const handleVisibleLinks = (
    links: SidebarLinkProps[], // Initial array of links.
    // eslint-disable-next-line
    updateLinksState: (state: HiddenVisibleLinksObj) => void, // Method to update the links in the state.
    stubs: React.RefObject<Array<HTMLDivElement | null>>, // Links related to rendered ones (for height calculation).
    containerHeight: number, // Parent total height.
    minus: number, // Pixels to delete from available space (Other nav height / margin / padding etc.).
  ) => {
    const availableHeight = containerHeight - minus;
    const visibleItems: SidebarLinkProps[] = [];
    const hiddenItems: SidebarLinkProps[] = [];
    let totalHeight = 0;

    stubs.current &&
      stubs.current.forEach((stub, index) => {
        if (stub) {
          totalHeight += stub.clientHeight + 10; // Where 10 is a top margin for every link.

          totalHeight < availableHeight
            ? visibleItems.push(links[index])
            : hiddenItems.push(links[index]);
        }
      });

    updateLinksState({ visible: visibleItems, hidden: hiddenItems });
  };

  return (
    <Box
      sx={{
        mt: `${props.headerHeight ?? (props.propertyDropdownVisible ? 191 : 135)}px`,
        mb: `${props.footerHeight ?? 128}px`,
        height: `calc(100% - ${props.headerHeight ?? (props.propertyDropdownVisible ? 191 : 135)}px - ${props.footerHeight ?? 128}px)`, // Full height - header - footer.
      }}
      ref={containerRef}
    >
      <Box ref={toolsWrapperRef}>
        {Boolean(ApiProvider.permission('tools_view')) && (
          <>
            <SidebarTools links={tools} compactSidebarMenus={props.compactSidebarMenus} />
            <Divider sx={{ borderColor: 'typography.main', mt: '10px' }} />
          </>
        )}
      </Box>

      <Box ref={viewsWrapperRef}>
        <SidebarViews links={views} compactSidebarMenus={props.compactSidebarMenus} {...props} />
      </Box>

      {/* We use hidden stub links for height calculation. */}
      <Box
        sx={{
          visibility: 'hidden',
          clip: 'rect(1px, 1px, 1px, 1px)',
          height: '1px',
          overflow: 'hidden',
          position: 'absolute',
          width: '1px',
        }}
      >
        {toolsLinks.map(({ icon, text, boxContainerStyles }, index) => (
          <Box
            key={index}
            // @ts-expect-error: We need this not to cause link errors.
            ref={(el) => (stubToolsRef.current[index] = el)}
          >
            <SidebarLink icon={icon} text={text} boxContainerStyles={boxContainerStyles} />
          </Box>
        ))}
        {viewsLinks.map(({ icon, text, boxContainerStyles }, index) => (
          <Box
            key={index}
            // @ts-expect-error: We need this not to cause link errors.
            ref={(el) => (stubViewsRef.current[index] = el)}
          >
            <SidebarLink icon={icon} text={text} boxContainerStyles={boxContainerStyles} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DynamicContentSections;
